/**
 * useChangeSignal - Real-time data sync across users via Firestore change signals.
 *
 * When a user mutates data (create/update/delete event, activity, etc.),
 * a "change signal" is written to Firestore: changeSignals/{artistId}
 *
 * All other users listening via onSnapshot will detect the change and
 * re-fetch data from Data Connect — but KEEP the old data visible until
 * the fresh data is fully loaded (stale-while-revalidate).
 *
 * Works offline: Firestore persistence ensures cached signals survive,
 * and React Query's keepPreviousData ensures old data stays visible.
 *
 * Usage:
 *   const { emitChange } = useChangeSignal();
 *
 *   await createActivity(dataConnect, { ... });
 *   refreshData();               // Local invalidation (instant)
 *   emitChange(artistId);        // Notify other users via Firestore
 */

import { useEffect, useCallback } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { useQueryClient } from '@tanstack/react-query';
import { db } from '../firebaseConfig';
import { useAuth } from '../contexts/AuthContext';
import Logger from '../utils/Logger';

/**
 * Emit a change signal for a given artist.
 * Called after a successful mutation to notify all users.
 */
export const emitChangeSignal = async (artistId, section = 'all') => {
    if (!artistId) return;

    try {
        const signalRef = doc(db, 'changeSignals', artistId);
        await setDoc(signalRef, {
            lastModified: serverTimestamp(),
            section,
            updatedAt: new Date().toISOString(), // Client timestamp for immediate comparison
        }, { merge: true });
        Logger.debug('ChangeSignal', `Emitted for artist=${artistId} section=${section}`);
    } catch (error) {
        // Non-critical — local user already has fresh data via refreshData()
        Logger.warn('ChangeSignal', `Failed to emit for ${artistId}:`, error);
    }
};

// Last signal seen per artist, kept across app launches so changes made
// while the app was closed are picked up on the first snapshot after start.
const SEEN_KEY = 'tm_change_signals_seen';

const loadSeenSignals = () => {
    try {
        return JSON.parse(localStorage.getItem(SEEN_KEY)) || {};
    } catch {
        return {};
    }
};

const saveSeenSignals = (seen) => {
    try {
        localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
    } catch {
        // Storage unavailable — worst case we miss one refetch after restart
    }
};

/**
 * Hook to listen for change signals on the user's active artists.
 * When a signal is detected from another user/device, it invalidates
 * React Query caches to trigger a background re-fetch.
 *
 * Debounces listener setup to avoid rapid attach/detach cycles that
 * can crash the Firestore SDK (known issue in firebase 12.x).
 *
 * @param {string[]} artistIds - Array of artist IDs to listen to
 */
const useChangeSignal = (artistIds = []) => {
    const { user } = useAuth();
    const queryClient = useQueryClient();

    // Stable string key to avoid re-triggering useEffect on every render
    const artistIdsKey = (artistIds || []).filter(Boolean).sort().join(',');

    useEffect(() => {
        if (!user || !artistIdsKey) return;

        const ids = artistIdsKey.split(',');
        let cancelled = false;
        const unsubscribes = [];

        // Debounce: wait 1s before attaching listeners to let the component stabilize
        const timer = setTimeout(() => {
            if (cancelled) return;

            for (const artistId of ids) {
                try {
                    const signalRef = doc(db, 'changeSignals', artistId);

                    const unsub = onSnapshot(signalRef, (snapshot) => {
                        if (cancelled || !snapshot.exists()) return;

                        const data = snapshot.data();
                        const signalTime = data.updatedAt || data.lastModified?.toMillis?.() || 0;
                        const seen = loadSeenSignals();
                        const prevTime = seen[artistId];
                        seen[artistId] = signalTime;
                        saveSeenSignals(seen);

                        // React to anything we haven't seen — also changes made while the app was closed.
                        // The very first time on this device there is nothing to compare with.
                        if (prevTime !== undefined && signalTime !== prevTime) {
                            Logger.debug('ChangeSignal', `Change detected for artist=${artistId}, section=${data.section}`);

                            // Mark everything stale but only refetch what is on screen right now;
                            // other pages refetch when opened. Old data stays visible until new data arrives.
                            queryClient.invalidateQueries();
                        }
                    }, (error) => {
                        // Gracefully handle Firestore internal errors (known SDK issue)
                        Logger.warn('ChangeSignal', `Snapshot error for ${artistId}:`, error?.message || error);
                    });

                    unsubscribes.push(unsub);
                } catch (error) {
                    // Catch any synchronous errors from onSnapshot setup
                    Logger.warn('ChangeSignal', `Failed to setup listener for ${artistId}:`, error?.message || error);
                }
            }
        }, 1000);

        return () => {
            cancelled = true;
            clearTimeout(timer);
            unsubscribes.forEach(unsub => {
                try { unsub(); } catch (e) { /* ignore cleanup errors */ }
            });
        };
    }, [user, artistIdsKey, queryClient]);

    /** Emit a change signal — call this after refreshData() */
    const emitChange = useCallback((artistId, section = 'all') => {
        return emitChangeSignal(artistId, section);
    }, []);

    return { emitChange };
};

export default useChangeSignal;
